import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaLock, FaSave, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import { useAuth } from '../../context/AuthContext';
import AuthService from '../../services/authService';
import '../../styles/StudentProfile.css';

const StudentProfile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await AuthService.getProfile(user.id);
      setProfile(response.data);
      setFormData(prev => ({
        ...prev,
        email: response.data.email || '',
        phone: response.data.phone || ''
      }));
    } catch (error) {
      toast.error('Failed to load profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    if (showPasswordForm) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required';
      }
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = 'Password must be at least 6 characters';
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const updateData = {
        email: formData.email,
        phone: formData.phone
      };
      
      if (showPasswordForm && formData.newPassword) {
        updateData.password = formData.newPassword;
      }
      
      const response = await AuthService.updateProfile(user.id, updateData);
      toast.success('Profile updated successfully');
      
      // Update local user data
      const updatedUser = { ...user, email: formData.email };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setShowPasswordForm(false);
      
      // Reload profile
      await loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="student-profile-loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="student-profile-page">
        <Container>
          {/* Back Button */}
          <Button
            variant="link"
            className="student-profile-back-btn"
            onClick={() => navigate('/student')}
          >
            <FaArrowLeft className="me-2" /> Back to Dashboard
          </Button>

          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="student-profile-card">
                <Card.Header className="student-profile-header">
                  <div className="student-profile-avatar">
                    <FaUser size={50} />
                  </div>
                  <h2 className="student-profile-name">{profile?.name}</h2>
                  <p className="student-profile-role">Student</p>
                </Card.Header>
                
                <Card.Body className="student-profile-body">
                  <Form onSubmit={handleSubmit}>
                    <div className="student-profile-section">
                      <h5 className="student-profile-section-title">Basic Information</h5>
                      <hr />
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={profile?.name}
                          disabled
                          className="student-profile-readonly-field"
                        />
                        <Form.Text className="text-muted">
                          Name cannot be changed. Contact admin for assistance.
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Email Address *</Form.Label>
                        <div className="student-profile-input-icon-wrapper">
                          <FaEnvelope className="student-profile-input-icon" />
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            className="student-profile-form-control"
                            placeholder="Enter your email"
                          />
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number *</Form.Label>
                        <div className="student-profile-input-icon-wrapper">
                          <FaPhone className="student-profile-input-icon" />
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            isInvalid={!!errors.phone}
                            className="student-profile-form-control"
                            placeholder="Enter 10-digit mobile number"
                          />
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {errors.phone}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    {/* Password Change Section */}
                    <div className="student-profile-section mt-4">
                      <div className="student-profile-password-header">
                        <h5 className="student-profile-section-title">Change Password</h5>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => setShowPasswordForm(!showPasswordForm)}
                        >
                          {showPasswordForm ? 'Cancel' : 'Change Password'}
                        </Button>
                      </div>
                      <hr />
                      
                      {showPasswordForm && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>Current Password *</Form.Label>
                            <div className="student-profile-input-icon-wrapper">
                              <FaLock className="student-profile-input-icon" />
                              <Form.Control
                                type="password"
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                isInvalid={!!errors.currentPassword}
                                className="student-profile-form-control"
                                placeholder="Enter current password"
                              />
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {errors.currentPassword}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>New Password *</Form.Label>
                            <div className="student-profile-input-icon-wrapper">
                              <FaLock className="student-profile-input-icon" />
                              <Form.Control
                                type="password"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                isInvalid={!!errors.newPassword}
                                className="student-profile-form-control"
                                placeholder="Enter new password (min 6 characters)"
                              />
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {errors.newPassword}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Confirm New Password *</Form.Label>
                            <div className="student-profile-input-icon-wrapper">
                              <FaLock className="student-profile-input-icon" />
                              <Form.Control
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                isInvalid={!!errors.confirmPassword}
                                className="student-profile-form-control"
                                placeholder="Confirm new password"
                              />
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {errors.confirmPassword}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </>
                      )}
                    </div>

                    <div className="student-profile-actions">
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={saving}
                        className="student-profile-save-btn"
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" /> Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default StudentProfile;